/**
 * The company password policy, for live feedback while typing.
 *
 * **The server is authoritative.** It re-validates every password and returns
 * field-level issues, so a disagreement between this and the API surfaces as a
 * rejected submit rather than a weak password getting through.
 *
 * This exists anyway because a checklist that updates as you type is worth far
 * more than one that appears after a failed submit — Nielsen's visibility of
 * system status. Making that round-trip to the server on every keystroke would
 * be both slow and pointless.
 *
 * The rules are duplicated from `orbit-api/src/shared/constants/auth.ts`. That
 * is a deliberate, small duplication: the two repos share a contract generated
 * from OpenAPI (ADR 0004), and a regex is not expressible there.
 */

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;

export interface PasswordRule {
  id: string;
  label: string;
  satisfied: boolean;
}

export interface PasswordStrength {
  valid: boolean;
  rules: PasswordRule[];
  /** 0–4, for the strength bar. Not a security measure — just feedback. */
  score: number;
}

export function evaluatePassword(password: string): PasswordStrength {
  const rules: PasswordRule[] = [
    {
      id: 'length',
      label: `At least ${PASSWORD_MIN_LENGTH} characters`,
      satisfied: password.length >= PASSWORD_MIN_LENGTH && password.length <= PASSWORD_MAX_LENGTH,
    },
    { id: 'uppercase', label: 'One uppercase letter', satisfied: /[A-Z]/.test(password) },
    { id: 'lowercase', label: 'One lowercase letter', satisfied: /[a-z]/.test(password) },
    { id: 'number', label: 'One number', satisfied: /\d/.test(password) },
    {
      id: 'symbol',
      label: 'One special character',
      satisfied: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(password),
    },
  ];

  const met = rules.filter((rule) => rule.satisfied).length;
  const allMet = met === rules.length;

  /**
   * The top mark needs real length as well as every rule.
   *
   * `Aa1@abcd` satisfies all five rules in eight characters, and showing a
   * full bar for it would tell the user it is excellent when it is merely
   * permitted. Length is what actually makes a password hard to guess, so the
   * last point is reserved for it.
   */
  const LONG_ENOUGH_FOR_TOP_SCORE = 12;

  return {
    valid: allMet,
    rules,
    score:
      allMet && password.length >= LONG_ENOUGH_FOR_TOP_SCORE
        ? 4
        : Math.min(3, Math.max(0, met - 1)),
  };
}
